// @flow
/**
 * User: Mike Chung
 * Date: 16/1/2018
 * Time: 11:15 PM
 */
import React, {Fragment} from 'react';
import {Form, reduxForm} from "redux-form";
import {Badge, Button} from "reactstrap";
import ReduxFormTextField from "./form/ReduxFormTextField";
import ReduxFormSelectField from "./form/ReduxFormSelectField";
import FairConfig from "../model/fair/FairConfig";
import * as _ from "lodash";
import {Country} from "../model/Country";
import config from '../config/config';
import ReduxFormCheckboxGroup, {ReduxFormCheckboxGroupItem} from "./form/ReduxFormCheckboxGroup";
import FormSelectConstants,{validateReadableRefIdLength}from "./constants/SearchFormConstants"
import '../components/ebadge/searchForm.css';

type SearchFormProps = {
    handleSubmit: (e: SyntheticEvent<HTMLFormElement>) => any,
    clearResult: () => any,
    fairConfigs: Array<FairConfig>,
    isLoading: boolean,
    countries: Array<Country>,
    onQrCodeScan?: (code: string) => any
};

const validateEmail = value => {
    const message = "Please input at least one character before and three characters after @.";
    if (!value) {
        return undefined;
    } else {
        if (value.split('@').length === 2) {
            if (!(/([^@]{1,}@[^@]{3,})/.test(value))) {
                return message;
            } else {
                return undefined;
            }
        } else {
            return message;
        }
    }
};

const validateRegistrationCode = value => {
    const message = "Registration Code should be 15 or 16 digits ";
    if (!value) {
        return undefined;
    } else {
        if (value.trim().length > 15) {
            const result = handleAdmissionCode(value);
            if (!result.isValid){
                return result.message;
            }
            value = result.extractedValue;
        }

        if (value.trim().length === 15 || value.trim().length === 16) {
            return undefined;
        } else {
            return message;
        }
    }
};


const getRegistrationCode = code => {
    if (code && code.trim().length > 15) {
        return handleAdmissionCode(code).extractedValue;
    } else {
        return code;
    }
}

const handleAdmissionCode = code => {
    const trimmedCode = code.trim();

    if (trimmedCode.length === 16) {
        return {
            isValid: true,
            message: '',
            extractedValue: code,
        };
    }

    // validate formatter
    const parts = trimmedCode.split('.');
    // rule two: must have five
    if (parts.length !== 5) {
        return {
            isValid: false,
            message: `Admission code format error: 5 parts are required, currently${parts.length}`,
            extractedValue: null,
        };
    }
    //Extract part 4 value
    const extractedValue = parts[3];
    return {
        isValid: true,
        message: '',
        extractedValue: extractedValue,
        errorType: null
    };

}

const html5QrCode = new window.Html5Qrcode("reader");
const qrCodeScannerConfig = { fps: 10, qrbox: { width: 500, height: 500 } };

function SearchForm(props: SearchFormProps) {
    return (
        <Form onSubmit={props.handleSubmit} className={"mb-3"}>
            <div className="form-row">
                <div className="col-lg">
                    <ReduxFormTextField className={"form-control"} maxLength={150} type={"text"} name={"email"} id={"email"} label={"Email"}
                                        placeholder={"Please input the email"} warn={validateEmail} validate={validateEmail}/>

                    <ReduxFormTextField maxLength={500} type={"text"} name={"companyName"} id={"companyName"} label={"Company Name"}
                                        placeholder={"Please input the Company Name"}/>
                    <ReduxFormTextField maxLength={200} type={"text"} name={"firstName"} id={"firstName"} label={"First Name"}
                                        placeholder={"Please input the First Name"}/>
                    <ReduxFormTextField maxLength={200} type={"text"} name={"lastName"} id={"lastName"} label={"Last Name"}
                                        placeholder={"Please input the Last Name"}/>
                    <ReduxFormSelectField type={"select"} name={"ebadgeType"} id={"ebadgeType"}
                                          label={<Fragment>Type of Registration <Badge color={"primary"}>Registration only</Badge></Fragment>}
                                          placeholder={"Please Select"}>
                        <option value={""}>Please Select:</option>
                        <option value={"BUYER"}>Buyer [All/00-59]</option>
                        <option value={"EXHIBITOR"}>Exhibitor [All/80]</option>
                        <option value={"STORAGE"}>Storage [All/81]</option>
                        <option value={"VIP"}>VIP [All/61-63]</option>
                        <option value={"MEDIA"}>Media [All/60]</option>
                        <option value={"ORGANISER"}>Organizer [All/A1-A4,A6]</option>
                        <option value={"OTHERS"}>Others [All/Others]</option>
                        <option value={"ALL"}>All [All/All]</option>

                    </ReduxFormSelectField>
                    <ReduxFormTextField maxLength={12} type={"text"} name={"readableRefId"} id={"readableRefId"}
                                        label={<Fragment>readableRefId </Fragment>} warn={validateReadableRefIdLength} validate={validateReadableRefIdLength}
                                        placeholder={"Please input the readableRefId"}/>
                    <ReduxFormSelectField type={"select"} name={"isVerifiedData"} id={"isVerifiedData"}
                                          label={<Fragment>verified data </Fragment>}
                                          placeholder={"Please Select the verified data"}>
                        <option value={""}>Please Select:</option>
                        {
                            _.map(FormSelectConstants.verifiedData, (item, index) => <option key={index} value={item.value}> {item.label}</option>)
                        }
                    </ReduxFormSelectField>
                </div>
                <div className="col-lg">
                    <ReduxFormSelectField type={"select"} name={"countryCode"} id={"countryCode"}
                                          label={<Fragment>Country <Badge color={"primary"}>Registration only</Badge></Fragment>}
                                          placeholder={"Please Select the Country"}>
                        <option value={""}>Please Select:</option>
                        {
                            _.map(props.countries, (country: Country, i: number) => <option key={i}
                                                                                            value={`${country.symbol}`}>{country.name}</option>)
                        }
                    </ReduxFormSelectField>
                    <ReduxFormTextField maxLength={16} type={"text"} name={"registrationCode"}
                                        id={"registrationCode"}
                                        label={<Fragment>Registration Code <Badge color={"primary"}>Registration
                                            only</Badge></Fragment>}
                                        placeholder={"Please input the Registration Code"}
                                        warn={validateRegistrationCode} validate={validateRegistrationCode}
                                        suffixButtonLabel={"Scan"} suffixButtonOnClick={(e) => {
                        e.preventDefault();
                        html5QrCode.start({facingMode: "environment"}, qrCodeScannerConfig, async (decodedText, result) => {
                            await html5QrCode.stop();
                            const errorMessage = validateRegistrationCode(decodedText);
                            const textCode = getRegistrationCode(decodedText);
                            if (errorMessage) {
                                alert(errorMessage);
                            } else {

                                props.onQrCodeScan && textCode &&props.onQrCodeScan(textCode);
                            }
                        })
                    }}/>

                    <ReduxFormSelectField type={"select"} name={"year"} id={"year"}
                                          label={<Fragment>Year <Badge color={"primary"}>Registration only</Badge></Fragment>}
                                          placeholder={"Please Select the Fair"}>
                        <option value={""}>Please Select:</option>
                        {
                            _.chain(props.fairConfigs).map((fairConfig: FairConfig): number => +fairConfig.year)
                                .uniq()
                                .orderBy([], ['desc'])
                                .map((year: string, i: number) => <option key={i} value={year}>{year}</option>).value()

                        }
                    </ReduxFormSelectField>
                    <ReduxFormSelectField type={"select"} name={"projectCode"} id={"projectCode"}
                                          label={<Fragment>Fair <Badge color={"primary"}>Registration only</Badge></Fragment>}
                                          placeholder={"Please Select the Fair"}>
                        <option value={""}>Please Select:</option>
                        {
                            _.chain(props.fairConfigs)
                                .map((fairConfig: FairConfig) => ({
                                    projectNumber: fairConfig.projectNumber,
                                    fairNames: fairConfig.fairNames,
                                    fairCode: fairConfig.fairCode
                                }))
                                .uniqBy((fc) => "" + fc.projectNumber)
                                .map((fc, i: number) => <option key={i}
                                                                value={`${fc.projectNumber}`}>{_.get(fc, "fairNames[0].name", fc.fairCode)}</option>)
                                .value()
                        }
                    </ReduxFormSelectField>
                    <ReduxFormTextField length={15} maxLength={15} type={"text"} name={"boothNumber"} id={"boothNumber"}
                                        label={<Fragment>Booth Number <Badge color={"primary"}>Registration only</Badge></Fragment>}
                                        placeholder={"Please input the Booth Number"}/>
                    <ReduxFormSelectField type={"select"} name={"status"} id={"status"}
                                          label={<Fragment>status</Fragment>}
                                          placeholder={"Please Select the redemption status"}>
                        <option value={""}>Please Select:</option>
                        {
                            _.map(FormSelectConstants.redemptionStatus, (item, index) => <option key={index} value={item.value}> {item.label}</option>)
                        }
                    </ReduxFormSelectField>
                    <div className={"row"}>
                        <div className={"col-md-6"}>
                            <ReduxFormCheckboxGroup label={"Source Type"} name={"sourceTypes"} id={"sourceTypes"} className={"scrollableCheckboxGroup"}>
                                {FormSelectConstants.sourceTypes.map((item, index) => (
                                    <ReduxFormCheckboxGroupItem
                                        key={item.value}
                                        label={item.label}
                                        value={item.value}
                                        selectAll={item.selectAll || false}
                                    />
                                ))}
                            </ReduxFormCheckboxGroup>
                        </div>
                        <div className={"col-md-6"}>
                            <ReduxFormCheckboxGroup label={"Visitor Type"} name={"visitorTypes"} id={"visitorTypes"} className={"scrollableCheckboxGroup"}>
                                {FormSelectConstants.visitorType.map((item, index) => (
                                    <ReduxFormCheckboxGroupItem
                                        key={item.value}
                                        label={item.label}
                                        value={item.value}
                                        selectAll={item.selectAll || false}
                                    />
                                ))}
                            </ReduxFormCheckboxGroup>
                        </div>
                    </div>
                </div>
            </div>
            <div>
                <Button disabled={props.isLoading} color={"primary"}>
                    <span>Search</span>
                </Button>{' '}
                {config.enableResetButton && <Button disabled={props.isLoading} color={"warning"} onClick={props.clearResult}>
                    <span>Reset</span>
                </Button>}
            </div>
        </Form>
    );
}

export default reduxForm({form: "EBadgeSearchForm"})(SearchForm);


// WEBPACK FOOTER //
// ./src/components/SearchForm.js